import { addFetchInstrumentationHandler } from '@sentry/core';
import * as Sentry from '@sentry/nextjs';
import 'client-only';

import { mirrorOnRootSpan } from '../utils/mirror-on-root-span';
import { sentryConfig } from '../utils/sentry-config';
import {
  API_401_ATTR,
  API_401_HEADER,
  API_401_MINT_ORIGINS,
  API_401_SPAN,
  type Api401MintOrigin,
  CLERK_AUTH_HEADER,
} from './constants';

/**
 * Attribution for every same-origin `/api/**` 401 the browser
 * sees.
 *
 * Each mint site stamps its reason onto the 401's response
 * headers — the middleware because its runtime has no Sentry
 * client, the route helpers for symmetry — and this reads
 * them back off the wire into one `api.401` span. A span per
 * 401 keeps concurrent failures from overwriting each other,
 * and the dimensions are mirrored onto the root span so
 * errors raised in the same window carry them.
 *
 * Deliberately no scope tags: a 401 is an event, not a
 * property of the page, and the browser's scope outlives it.
 *
 * Never throws: observability must not interrupt the request
 * it observes.
 */

// Survives a module re-evaluation (Fast Refresh, a duplicate
// chunk); module state would not, and each re-register would
// multiply the spans a single 401 emits.
const INSTALLED_FLAG = '__claiApi401ObserverInstalled';

function isMintOrigin(value: string | null): value is Api401MintOrigin {
  return (API_401_MINT_ORIGINS as readonly (string | null)[]).includes(value);
}

/**
 * Same-origin `/api/**` only. Supabase PostgREST and Clerk
 * FAPI also 401 through the patched `fetch`, and their
 * cross-origin headers are unreadable without
 * `Access-Control-Expose-Headers`, so they would land as
 * `unknown` and drown the signal that name is reserved for.
 */
function api401Path(url: string): string | null {
  try {
    const parsed = new URL(url, window.location.href);
    if (parsed.origin !== window.location.origin) return null;
    return parsed.pathname.startsWith('/api/') ? parsed.pathname : null;
  } catch {
    return null;
  }
}

/** First `/api/` segment, so no deal or flow UUID enters a tag. */
function routeGroupOf(pathname: string): string | undefined {
  return pathname.split('/')[2] || undefined;
}

function attributesOf(
  headers: Headers,
  pathname: string
): Record<string, string | boolean> {
  const stamped = headers.get(API_401_HEADER.ORIGIN);
  const origin = isMintOrigin(stamped) ? stamped : 'unknown';

  const attributes: Record<string, string | boolean> = {
    [API_401_ATTR.ORIGIN]: origin,
  };

  const routeGroup = routeGroupOf(pathname);
  if (routeGroup) attributes[API_401_ATTR.ROUTE_GROUP] = routeGroup;

  // Clerk's verdict describes the middleware's own resolution.
  // It rides every response the middleware touches, including
  // one where it admitted a signer and a handler then refused
  // them — reading it there would report a Clerk session
  // reason for a denial Clerk had no part in.
  if (origin === 'middleware') {
    const reason = headers.get(CLERK_AUTH_HEADER.REASON);
    if (reason) attributes[API_401_ATTR.REASON] = reason;

    const status = headers.get(CLERK_AUTH_HEADER.STATUS);
    if (status) attributes[API_401_ATTR.STATUS] = status;
  }

  // Denial codes are minted by resolveAuth → unauthorized().
  // Middleware 401s carry Clerk's reason/status instead; a denial
  // code on middleware would be a lie.
  if (origin === 'route') {
    const denialCode = headers.get(API_401_HEADER.DENIAL_CODE);
    if (denialCode) attributes[API_401_ATTR.DENIAL_CODE] = denialCode;
  }

  const refresh = headers.get(API_401_HEADER.REFRESH);
  if (refresh !== null) {
    attributes[API_401_ATTR.HAS_REFRESH_COOKIE] = refresh === '1';
  }

  return attributes;
}

function observe(headers: Headers, pathname: string): void {
  const attributes = attributesOf(headers, pathname);

  // Unconditional: the 401s worth explaining are the ones a
  // woken or backgrounded tab fires, and the browser idle
  // span has long since closed by then. Gating on an active
  // span would drop exactly that population.
  Sentry.startInactiveSpan({
    name: API_401_SPAN.NAME,
    op: API_401_SPAN.OP,
    attributes,
  }).end();

  mirrorOnRootSpan(attributes);
}

export function installApi401Observer(): void {
  // Self-guarding, like every sibling observer, so any future
  // install site is safe to call unconditionally.
  if (!sentryConfig.enabled) return;

  const flagged = globalThis as typeof globalThis & {
    [INSTALLED_FLAG]?: boolean;
  };
  if (flagged[INSTALLED_FLAG]) return;
  flagged[INSTALLED_FLAG] = true;

  try {
    // Registering installs Sentry's `fetch` patch when none
    // exists yet, so this is safe at any point in client
    // bootstrap. `@hidden` in the SDK, hence the exact version
    // pin on `@sentry/core` and the guard around the body.
    // Docs:
    //   https://docs.sentry.io/platforms/javascript/guides/nextjs/tracing/instrumentation/custom-instrumentation/
    addFetchInstrumentationHandler(data => {
      try {
        if (!data.endTimestamp) return;
        if (data.response?.status !== 401) return;
        const pathname = api401Path(data.fetchData.url);
        if (!pathname) return;
        observe(data.response.headers as Headers, pathname);
      } catch {
        // A 401 is already a bad moment; don't make it worse.
      }
    });
  } catch {
    // Sentry's fetch instrumentation is unavailable; the
    // observer stays a no-op.
  }
}
